import { FlatCard } from '@/components/FlatCard';
import { createDataTable } from '@/components/DataTable';
import ReactECharts from 'echarts-for-react';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './OverTimeCard.module.scss';
import { Button, ButtonGroup } from '@ygtechnologies/rubus-ui';
import { useThemePreference } from '@/components/ThemePreferenceContext';

interface ChartThemeColors {
    text: string;
    muted: string;
    line: string;
}

// echarts renders to canvas and can't consume CSS variables — resolve the theme
// tokens to concrete values, re-reading when the theme preference flips
function useChartThemeColors(): ChartThemeColors {
    const { themePreference } = useThemePreference();
    const [colors, setColors] = useState<ChartThemeColors>({ text: '#333', muted: '#6b7280', line: '#ccc' });

    useEffect(() => {
        const rootStyles = getComputedStyle(document.documentElement);
        const read = (name: string, fallback: string) => rootStyles.getPropertyValue(name).trim() || fallback;
        setColors({
            text: read('--rui-semantic-text-body', '#333'),
            muted: read('--rui-semantic-text-muted', '#6b7280'),
            line: read('--rui-semantic-border-subtle', '#ccc'),
        });
    }, [themePreference]);

    return colors;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatMonthLabel = (year: number, month: number) => `${MONTH_NAMES[month - 1]} ${year}`;

interface OverTimeTableRow {
    period: string;
    [key: string]: string | number;
}

const OverTimeDataTable = createDataTable<OverTimeTableRow>();

type WithYearMonth = { year: number; month: number };
type View = 'cumulative' | 'bar' | 'monthly' | 'yearly';
type BarGranularity = 'month' | 'year';

export interface OverTimeSeriesConfig<T extends WithYearMonth> {
    dataKey: Exclude<keyof T, 'year' | 'month'>;
    name: string;
    color: string;
}

interface OverTimeCardProps<T extends WithYearMonth> {
    cardId: string;
    title: string;
    data: T[] | null;
    series: OverTimeSeriesConfig<T>[];
    loading: boolean;
}

function OverTimeCard<T extends WithYearMonth>({ cardId, title, data, series, loading }: OverTimeCardProps<T>) {
    const [view, setView] = useState<View>('cumulative');
    const [barGranularity, setBarGranularity] = useState<BarGranularity>('month');
    const [tableState, setTableState] = useState(OverTimeDataTable.getInitialState('period'));

    // Per-month rows, with a yyyy-MM-01 timestamp for the time axis and a period label for tables.
    const monthlyRows = useMemo(() => {
        if (!data) return [];
        return data.map((row) => {
            const isoDate = `${row.year}-${String(row.month).padStart(2, '0')}-01`;
            const point: Record<string, number | string> = {
                date: isoDate,
                period: formatMonthLabel(row.year, row.month),
            };
            series.forEach((s) => {
                point[s.dataKey as string] = (row[s.dataKey] as number) ?? 0;
            });
            return point;
        });
    }, [data, series]);

    // Yearly aggregation — sum deltas across months for each series, anchored to yyyy-01-01.
    const yearlyRows = useMemo(() => {
        if (!data) return [];
        const yearMap = new Map<number, Record<string, number>>();
        data.forEach((row) => {
            if (!yearMap.has(row.year)) yearMap.set(row.year, {});
            const entry = yearMap.get(row.year)!;
            series.forEach((s) => {
                entry[s.dataKey as string] =
                    (entry[s.dataKey as string] ?? 0) + ((row[s.dataKey] as number) ?? 0);
            });
        });
        return Array.from(yearMap.entries())
            .sort(([a], [b]) => a - b)
            .map(([year, counts]) => ({
                date: `${year}-01-01`,
                period: String(year),
                ...counts,
            }));
    }, [data, series]);

    // For the chart: cumulative series along the time axis (one [date, value] pair per month per series).
    const cumulativeSeriesData = useMemo(() => {
        const accumulators: Record<string, number> = {};
        series.forEach((s) => { accumulators[s.dataKey as string] = 0; });
        return series.map((s) => ({
            name: s.name,
            data: monthlyRows.map((row) => {
                accumulators[s.dataKey as string] += (row[s.dataKey as string] as number) ?? 0;
                return [row.date as string, accumulators[s.dataKey as string]] as [string, number];
            }),
        }));
    }, [monthlyRows, series]);

    // For bar mode: raw deltas (not cumulative) at chosen granularity.
    const barSeriesData = useMemo(() => {
        const rows = barGranularity === 'month' ? monthlyRows : yearlyRows;
        return series.map((s) => ({
            name: s.name,
            data: rows.map((row) => [row.date as string, (row[s.dataKey as string] as number) ?? 0] as [string, number]),
        }));
    }, [monthlyRows, yearlyRows, series, barGranularity]);

    const paginatedMonthlyData = useMemo(() => {
        const rows = monthlyRows as unknown as OverTimeTableRow[];
        const start = tableState.pageNumber * tableState.pageSize;
        return rows.slice(start, start + tableState.pageSize);
    }, [monthlyRows, tableState.pageNumber, tableState.pageSize]);

    const chartColors = useChartThemeColors();

    const chartOption = useMemo(() => {
        const isBar = view === 'bar';
        const activeData = isBar ? barSeriesData : cumulativeSeriesData;
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: isBar ? 'shadow' : 'cross', label: { backgroundColor: '#6a7985' } },
            },
            legend: {
                data: series.map((s) => s.name),
                top: 0,
                textStyle: { fontSize: 11, color: chartColors.text },
            },
            grid: { left: 50, right: 24, top: 32, bottom: 60 },
            xAxis: {
                type: 'time',
                axisLabel: { fontSize: 10, hideOverlap: true, color: chartColors.muted },
            },
            yAxis: {
                type: 'value',
                axisLabel: { fontSize: 10, color: chartColors.muted },
                splitLine: { lineStyle: { type: 'dashed', color: chartColors.line } },
            },
            dataZoom: [
                { type: 'inside', start: 0, end: 100, zoomOnMouseWheel: 'shift', moveOnMouseWheel: false },
                { type: 'slider', height: 20, bottom: 8, start: 0, end: 100, showDetail: false },
            ],
            series: activeData.map((s, i) => {
                const color = series[i].color;
                return {
                    name: s.name,
                    type: isBar ? 'bar' : 'line',
                    smooth: !isBar,
                    showSymbol: false,
                    sampling: 'lttb',
                    barMinWidth: 2,
                    barMaxWidth: 24,
                    data: s.data,
                    itemStyle: { color },
                    lineStyle: !isBar ? { width: 2 } : undefined,
                    areaStyle: !isBar
                        ? {
                              opacity: 0.25,
                              color: {
                                  type: 'linear',
                                  x: 0, y: 0, x2: 0, y2: 1,
                                  colorStops: [
                                      { offset: 0, color: color },
                                      { offset: 1, color: 'rgba(0,0,0,0)' },
                                  ],
                              },
                          }
                        : undefined,
                };
            }),
        };
    }, [view, cumulativeSeriesData, barSeriesData, series, chartColors]);

    return (
        <FlatCard colSpan={2} rowSpan={2} loading={loading && !data} title={title}>
            {() => (
                <>
                    <div className={styles.toolbar}>
                        <ButtonGroup aria-label="Chart view">
                            <Button
                                size="xs"
                                variant={view === 'cumulative' ? 'primary' : 'tertiary'}
                                onClick={() => setView('cumulative')}>
                                Cumulative
                            </Button>
                            <Button
                                size="xs"
                                variant={view === 'bar' ? 'primary' : 'tertiary'}
                                onClick={() => setView('bar')}>
                                Bar
                            </Button>
                            <Button
                                size="xs"
                                variant={view === 'monthly' ? 'primary' : 'tertiary'}
                                onClick={() => setView('monthly')}>
                                By month
                            </Button>
                            <Button
                                size="xs"
                                variant={view === 'yearly' ? 'primary' : 'tertiary'}
                                onClick={() => setView('yearly')}>
                                By year
                            </Button>
                        </ButtonGroup>
                        {view === 'bar' && (
                            <div className={styles.toolbarRight}>
                                <span className={styles.toolbarLabel}>Group by:</span>
                                <ButtonGroup aria-label="Bar grouping">
                                    <Button
                                        size="xs"
                                        variant={barGranularity === 'month' ? 'primary' : 'tertiary'}
                                        onClick={() => setBarGranularity('month')}>
                                        Month
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant={barGranularity === 'year' ? 'primary' : 'tertiary'}
                                        onClick={() => setBarGranularity('year')}>
                                        Year
                                    </Button>
                                </ButtonGroup>
                            </div>
                        )}
                    </div>

                    {(view === 'cumulative' || view === 'bar') && (
                        <ReactECharts
                            key={`${cardId}-${view}-${barGranularity}`}
                            option={chartOption}
                            style={{ height: 280, width: '100%' }}
                            notMerge
                            lazyUpdate
                        />
                    )}

                    {(view === 'monthly' || view === 'yearly') && (
                        <div className={styles.compactTable}>
                            <OverTimeDataTable
                                data={view === 'monthly' ? paginatedMonthlyData : (yearlyRows as unknown as OverTimeTableRow[])}
                                state={tableState}
                                onStateChange={setTableState}
                                hasPagination={view === 'monthly'}
                                totalItemCount={view === 'monthly' ? monthlyRows.length : yearlyRows.length}
                                className="table style-3">
                                <OverTimeDataTable.Column header="Period" field="period" sortable />
                                {series.map((s) => (
                                    <OverTimeDataTable.Column
                                        key={s.dataKey as string}
                                        header={s.name}
                                        field={s.dataKey as string}
                                        sortable
                                    />
                                )) as unknown as React.ReactElement}
                            </OverTimeDataTable>
                        </div>
                    )}
                </>
            )}
        </FlatCard>
    );
}

export default OverTimeCard;
